/**
 * Office auth dialog page — opened via displayDialogAsync from the taskpane.
 * Uses the same Entra app (ffbf34bc) as web login; returns token via messageParent.
 */
import React, { useEffect, useState } from 'react';

import { InteractionRequiredAuthError } from '@azure/msal-browser';

import {
    getOfficeAuthRedirectUri,
    officeDialogLoginRequest,
    silentAuthRequest,
} from '../../../auth/authConfig';
import { msalInstance } from '../../../auth/msalInstance';
import type { OfficeAuthDialogResult } from './openOfficeAuthDialog';

const OFFICE_JS_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

function postResultToParent(result: OfficeAuthDialogResult): void {
    const officeUi = (Office as any)?.context?.ui;
    if (officeUi && typeof officeUi.messageParent === 'function') {
        officeUi.messageParent(JSON.stringify(result));
        return;
    }

    if (process.env.REACT_APP_DEBUG === 'true') {
        // eslint-disable-next-line no-console
        console.warn('[OfficeAuthDialog] messageParent unavailable', result);
    }
}

async function waitForOfficeJs(): Promise<void> {
    if (typeof Office !== 'undefined') {
        if (typeof (Office as any).onReady === 'function') {
            await (Office as any).onReady();
        }
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const existing = document.getElementById('office-js') as HTMLScriptElement | null;
        const script = existing || document.createElement('script');
        if (!existing) {
            script.id = 'office-js';
            script.src = OFFICE_JS_URL;
            script.async = true;
            document.head.appendChild(script);
        }
        script.onload = () => {
            void (Office as any).onReady?.().then(resolve).catch(reject);
        };
        script.onerror = () => reject(new Error('Office.js failed to load'));
    });
}

const OfficeAuthDialogEntry: React.FC = () => {
    const [message, setMessage] = useState('Signing in with Microsoft…');

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            try {
                await waitForOfficeJs();
                await msalInstance.initialize();

                const redirectResult = await msalInstance.handleRedirectPromise();
                if (cancelled) return;

                if (redirectResult?.accessToken) {
                    postResultToParent({
                        status: 'success',
                        accessToken: redirectResult.accessToken,
                    });
                    return;
                }

                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    try {
                        const silent = await msalInstance.acquireTokenSilent({
                            ...silentAuthRequest,
                            account: accounts[0],
                        });
                        if (silent.accessToken) {
                            postResultToParent({
                                status: 'success',
                                accessToken: silent.accessToken,
                            });
                            return;
                        }
                    } catch (silentError) {
                        if (!(silentError instanceof InteractionRequiredAuthError)) {
                            throw silentError;
                        }
                    }
                }

                setMessage('Redirecting to Microsoft…');
                await msalInstance.loginRedirect({
                    ...officeDialogLoginRequest,
                    redirectUri: getOfficeAuthRedirectUri(),
                });
            } catch (error) {
                const err = error as { errorCode?: string; message?: string };
                postResultToParent({
                    status: 'error',
                    code: err.errorCode,
                    message: err.message || 'Microsoft sign-in failed',
                });
                if (!cancelled) {
                    setMessage(err.message || 'Sign-in failed');
                }
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div
            style={{
                fontFamily: 'Segoe UI, system-ui, sans-serif',
                padding: 24,
                textAlign: 'center',
                color: '#323130',
            }}
        >
            <p>{message}</p>
        </div>
    );
};

export default OfficeAuthDialogEntry;
